import { useState } from 'react';
import { Button } from '@/components/ui/button';

/**
 * AOZONE-104: a code block plus a Copy button, used for the `claude mcp add`
 * command, the JSON client config and the bare token. Extracted from
 * GenerateMcpTokenButton so the reveal panel and the persistent instructions
 * share one copy affordance.
 *
 * The snippet text is a single text node inside the `<pre>` so its `textContent`
 * is exactly what would be copied — tests assert that byte for byte, since the
 * command's line continuations are load-bearing.
 */
interface CopyableSnippetProps {
  text: string;
  /** Accessible name of the copy button; must be unique within a page. */
  copyLabel: string;
  testId: string;
  /** Amber (inside the one-time reveal panel) vs. neutral (static docs). */
  tone?: 'neutral' | 'amber';
  /** Wrap instead of scrolling horizontally — for a long unbroken token. */
  wrap?: boolean;
}

export function CopyableSnippet({
  text,
  copyLabel,
  testId,
  tone = 'neutral',
  wrap = false,
}: CopyableSnippetProps) {
  const [copied, setCopied] = useState<'idle' | 'ok' | 'failed'>('idle');

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied('ok');
    } catch {
      // Clipboard can reject (insecure context / denied permission); fall back
      // to asking the user to copy the visible text manually.
      setCopied('failed');
    }
  };

  return (
    <div>
      <div className="flex items-start gap-2">
        <pre
          data-testid={testId}
          className={[
            'flex-1 rounded px-2 py-1 font-mono text-xs text-slate-800',
            tone === 'amber' ? 'bg-white' : 'bg-slate-100',
            wrap ? 'whitespace-pre-wrap break-all' : 'overflow-x-auto',
          ].join(' ')}
        >
          {text}
        </pre>
        <Button type="button" onClick={onCopy} variant="outline" size="sm">
          {copied === 'ok' ? 'Copied' : copyLabel}
        </Button>
      </div>
      {/* Announced, not just recoloured: the Copy button's label flip is the only
          other feedback, and the failure case matters more than the success one. */}
      <div role="status" aria-live="polite">
        {copied === 'ok' && (
          <div className="sr-only">{copyLabel}: copied to your clipboard.</div>
        )}
        {copied === 'failed' && (
          <div className={`mt-2 text-xs ${tone === 'amber' ? 'text-amber-700' : 'text-slate-500'}`}>
            Couldn&rsquo;t copy automatically — select the text above and copy it manually.
          </div>
        )}
      </div>
    </div>
  );
}
